/**
 * Second-factor policy (ADR-015 §7, §5.7, §5.8). Decided by permissions, never by the name of a
 * role: a custom role that can manage users is administrative whatever it is called.
 */
import { SYSTEM_ROLE_DEFINITIONS, type PermissionKey } from '../rbac/permissions';
import type { SessionAudience } from './session-policy';

/** Holding any of these makes the second factor mandatory. */
export const MFA_MANDATORY_PERMISSIONS = [
  'users.manage',
  'users.reset_access',
  'roles.manage',
  'settings.manage',
  'integrations.manage',
  'security.manage',
] as const satisfies readonly PermissionKey[];

export type MfaReason =
  /** Platform operators always use a second factor. */
  | 'PLATFORM'
  /** `users.mfa_required` set for this person. */
  | 'USER_POLICY'
  /** Holds one of MFA_MANDATORY_PERMISSIONS. */
  | 'ADMIN_PERMISSION'
  /** One of the person's roles is in `tenant_settings.mfa_required_roles`. */
  | 'TENANT_POLICY'
  /** Not mandatory, but the person enrolled a factor: it is verified on every sign-in. */
  | 'ENROLLED'
  | 'NOT_REQUIRED';

/** What the sign-in has to do: nothing, verify the existing factor, or enroll one first. */
export type MfaStep = 'NONE' | 'VERIFY' | 'SETUP';

export interface MfaInput {
  audience: SessionAudience;
  permissions: ReadonlySet<string>;
  roleKeys: readonly string[];
  /** `tenant_settings.mfa_required_roles` */
  tenantRequiredRoles: readonly string[];
  /** `users.mfa_required` */
  userMfaRequired: boolean;
  hasConfirmedFactor: boolean;
}

export interface MfaRequirement {
  /** The policy demands a second factor (independent of whether one is enrolled). */
  required: boolean;
  reason: MfaReason;
  step: MfaStep;
}

const DRIVER_PERMISSIONS = new Set<string>(SYSTEM_ROLE_DEFINITIONS.DRIVER.permissions);

/** Somebody who only uses the driver app: holds `driver.app` and nothing beyond the driver role. */
export function isDriverOnly(permissions: ReadonlySet<string>): boolean {
  if (!permissions.has('driver.app')) return false;
  for (const p of permissions) if (!DRIVER_PERMISSIONS.has(p)) return false;
  return true;
}

function policy(input: MfaInput): { required: boolean; reason: MfaReason } {
  if (input.audience === 'platform') return { required: true, reason: 'PLATFORM' };
  if (input.userMfaRequired) return { required: true, reason: 'USER_POLICY' };
  if (MFA_MANDATORY_PERMISSIONS.some((p) => input.permissions.has(p))) return { required: true, reason: 'ADMIN_PERMISSION' };
  // drivers are never forced by the tenant policy, only individually (userMfaRequired)
  if (isDriverOnly(input.permissions)) return { required: false, reason: 'NOT_REQUIRED' };
  if (input.roleKeys.some((r) => input.tenantRequiredRoles.includes(r))) return { required: true, reason: 'TENANT_POLICY' };
  return { required: false, reason: 'NOT_REQUIRED' };
}

export function mfaRequirement(input: MfaInput): MfaRequirement {
  const { required, reason } = policy(input);
  if (input.hasConfirmedFactor) return { required, reason: required ? reason : 'ENROLLED', step: 'VERIFY' };
  return { required, reason, step: required ? 'SETUP' : 'NONE' };
}

/**
 * An ACTIVE account the policy now obliges to use a second factor it does not have (SEC-E1-1). It
 * cannot enroll the factor at sign-in with the password alone: the first factor is only enrolled
 * inside an invitation / activation or with a WhatsApp code (MFA_ENROLL). Admin screens mark these
 * accounts so the administrator sends a new invitation. Never true for INVITED accounts (their
 * activation enrolls it) nor for suspended or disabled ones.
 */
export function mfaSetupPending(input: MfaInput & { status: string }): boolean {
  return input.status === 'ACTIVE' && mfaRequirement(input).step === 'SETUP';
}

/** After a password recovery the authenticator cannot be enrolled with a WhatsApp code for 72 h (SEC-D-5). */
export const MFA_ENROLL_AFTER_RECOVERY_MS = 72 * 3_600_000;

export type OtpEnrollDecision = { ok: true } | { ok: false; reason: 'PHONE_NOT_VERIFIED' } | { ok: false; reason: 'RECENT_RECOVERY'; retryAt: Date };

/** MFA enrollment outside the invitation, proven with an OTP to the verified phone (ADR-015 §5.8). */
export function canEnrollWithOtp(input: { phoneVerifiedAt: Date | null; lastRecoveryAt: Date | null; now: Date }): OtpEnrollDecision {
  if (!input.phoneVerifiedAt) return { ok: false, reason: 'PHONE_NOT_VERIFIED' };
  if (input.lastRecoveryAt) {
    const retryAt = new Date(input.lastRecoveryAt.getTime() + MFA_ENROLL_AFTER_RECOVERY_MS);
    if (input.now.getTime() < retryAt.getTime()) return { ok: false, reason: 'RECENT_RECOVERY', retryAt };
  }
  return { ok: true };
}

/** After a phone change, the same admin cannot also reset the MFA for 24 h (SEC-D-2). */
export const MFA_RESET_AFTER_PHONE_CHANGE_MS = 24 * 3_600_000;

/**
 * Resetting the factors right after changing the number would let one admin take the account alone:
 * within 24 h it has to be a different actor. Applies on top of `canManage(…, 'resetMfa')`.
 *
 * @deprecated One direction of the pair only (SEC-E2-1). Use `canChangeAccess` (access-change.ts),
 * which applies the same rule to both orders and never expires for sensitive accounts.
 */
export function canResetMfaAfterPhoneChange(input: { actorId: string; phoneChangedBy: string | null; phoneChangedAt: Date | null; now: Date }): boolean {
  if (!input.phoneChangedAt) return true;
  if (input.now.getTime() - input.phoneChangedAt.getTime() >= MFA_RESET_AFTER_PHONE_CHANGE_MS) return true;
  // unknown author of the change: wait out the window
  return input.phoneChangedBy !== null && input.phoneChangedBy !== input.actorId;
}
